import { fetch, Proxy } from '@skinsoft/network';
import { ObservableService } from '@skinsoft/rxstate';
import {
    animationFrameScheduler,
    BehaviorSubject,
    race,
    SchedulerLike,
    Subscription,
    throwError,
} from 'rxjs';
import { catchError, first, switchMapTo } from 'rxjs/operators';

import { AuthContext, AuthEvent, AuthStateSchema, createMachine } from './machine';
import { User, UserDataStore } from './user-data-store';

export class AuthService extends ObservableService<AuthContext, AuthEvent, AuthStateSchema> {
    public static getInstance(
        storage: Storage = window.localStorage,
        proxy = new Proxy(false),
        scheduler?: SchedulerLike,
    ) {
        if (!AuthService.instance) {
            AuthService.instance = new AuthService(storage, proxy, scheduler);
        }

        return AuthService.instance;
    }

    public static install(Vue) {
        Vue.prototype.$authService = AuthService.getInstance(
            window.localStorage,
            new Proxy(false),
            animationFrameScheduler,
        );
    }

    private static instance: AuthService = null as any;

    public readonly user$ = new BehaviorSubject<User | null>(null);

    private proxy: Proxy;
    private storage: Storage;
    private userSubscription: Subscription;

    constructor(
        storage: Storage = window.localStorage,
        proxy = new Proxy(false),
        scheduler?: SchedulerLike,
    ) {
        super(
            createMachine({
                beginChallenge: (_, event: AuthEvent) => this.beginChallenge(event),
                clearStorage: () => this.clearStorage(),
                startUserPolling: (context: AuthContext) => this.startUserPolling(context),
                stopUserPolling: () => this.stopUserPolling(),
                storeUserId: (context: AuthContext, event: AuthEvent) => {
                    const { userId } = event.query as { userId: string };
                    context.userId = userId;
                },
            }),
            scheduler,
        );

        this.storage = storage;
        this.proxy = proxy;
    }

    public async logIn(username: string, password: string) {
        this.sendEvent({ type: 'CHALLENGE', query: { username, password } });
        await this.waitFor$('challenging').toPromise();
    }

    public logOut(error?: Error) {
        this.sendEvent({ type: 'DEAUTHENTICATE', query: { error } });
    }

    public waitForUser$() {
        return race(this.waitFor$('authenticated'), this.waitFor$('unauthenticated')).pipe(
            first(),
            switchMapTo(this.user$),
        );
    }

    protected boot() {
        const token = this.storage.getItem('token');
        const userId = this.storage.getItem('userId');

        if (token && userId) {
            fetch.registerToken(token);
            this.sendEvent({ type: 'AUTHENTICATE', query: { userId } });
            return;
        }

        this.sendEvent('DEAUTHENTICATE');
    }

    private beginChallenge(event: AuthEvent) {
        const login$ = this.proxy.fetch('/login', undefined, 'POST', event.query);

        login$.subscribe({
            error: error => {
                this.sendEvent({ type: 'DEAUTHENTICATE', query: { error } });
            },
            next: ({ token, userId }) => {
                this.storage.setItem('token', token);
                this.storage.setItem('userId', userId);

                fetch.registerToken(token);

                this.sendEvent({ type: 'AUTHENTICATE', query: { userId } });
            },
        });
    }

    private startUserPolling(context: AuthContext) {
        const user$ = UserDataStore.getInstance()
            .fetchOne(context.userId as string, true)
            .pipe(
                catchError(error => {
                    if (error.status === 401) {
                        this.logOut();
                    }

                    return throwError(error);
                }),
            );
        this.userSubscription = user$.subscribe(user => this.user$.next(user));
    }

    private stopUserPolling() {
        if (this.userSubscription) {
            this.userSubscription.unsubscribe();
        }

        this.user$.next(null);
    }

    private clearStorage() {
        this.storage.removeItem('token');
        this.storage.removeItem('userId');
        this.storage.removeItem('workspace');
        this.storage.removeItem('institution');
        fetch.registerToken(null);
    }
}
